import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, ShieldCheck } from "lucide-react";
import { AFRICAN_CITIES_FALLBACK, AFRICAN_COUNTRIES } from "@/lib/countries";

const CITIES = AFRICAN_CITIES_FALLBACK;

const statusBadge = (s) => {
  const map = {
    unverified: "bg-secondary text-foreground",
    requested: "bg-amber-500 text-white",
    assigned: "bg-amber-600 text-white",
    in_progress: "bg-blue-600 text-white",
    verified: "bg-primary text-primary-foreground",
    rejected: "bg-red-700 text-white",
  };
  return <span className={`mono text-[10px] px-2 py-1 rounded-sm uppercase ${map[s] || "bg-secondary"}`}>{s}</span>;
};

export default function SellerDashboard() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    make: "", model: "", year: 2020, mileage_km: 0, price_usd: 0,
    transmission: "manual", fuel: "petrol", country: "", city: "", description: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/vehicles/mine");
      setItems(r.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vehicles", {
        ...form,
        year: Number(form.year),
        mileage_km: Number(form.mileage_km),
        price_usd: Number(form.price_usd),
      });
      toast.success("Listing created");
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const uploadPhotos = async (vid, files) => {
    setUploading(vid);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("module_key", "auto");
        fd.append("case_id", vid);
        fd.append("category", "vehicle.listing");
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      await api.post(`/vehicles/${vid}/photos`, ids);
      toast.success(`${ids.length} photo(s) uploaded`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const requestVerification = async (vid) => {
    try {
      await api.post(`/vehicles/${vid}/request-verification`);
      toast.success("Verification requested — an inspector will be assigned by an admin.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="kicker">Seller Portal</div>
            <h1 className="display text-4xl">Your listings</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="seller-new-listing-btn"><Plus className="h-4 w-4 mr-1" /> New listing</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create vehicle listing</DialogTitle></DialogHeader>
              <form onSubmit={create} className="grid grid-cols-2 gap-3">
                <div className="col-span-1"><Label>Make</Label><Input required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="listing-make" /></div>
                <div className="col-span-1"><Label>Model</Label><Input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="listing-model" /></div>
                <div><Label>Year</Label><Input type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="listing-year" /></div>
                <div><Label>Mileage (km)</Label><Input type="number" required value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} data-testid="listing-mileage" /></div>
                <div><Label>Price (USD)</Label><Input type="number" required value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} data-testid="listing-price" /></div>
                <div>
                  <Label>Country</Label>
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger data-testid="listing-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent>{AFRICAN_COUNTRIES.map((c) => <SelectItem key={c.iso} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>City</Label>
                  <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                    <SelectTrigger data-testid="listing-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Transmission</Label>
                  <Select value={form.transmission} onValueChange={(v) => setForm({ ...form, transmission: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fuel</Label>
                  <Select value={form.fuel} onValueChange={(v) => setForm({ ...form, fuel: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="petrol">Petrol</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="electric">Electric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="listing-create-submit">
                    {busy ? "Creating…" : "Create listing"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {items.length === 0 ? (
          <div className="p-16 border border-border text-center">
            <div className="display text-2xl">No listings yet</div>
            <p className="text-muted-foreground mt-2">Add a vehicle, upload photos, then request a THOM Verification.</p>
          </div>
        ) : (
          <div className="border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary/60">
                <tr>
                  <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Vehicle</th>
                  <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Price</th>
                  <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Photos</th>
                  <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} className="border-t border-border" data-testid={`seller-listing-${v.id}`}>
                    <td className="py-3 px-3">
                      <div className="font-medium">{v.year} {v.make} {v.model}</div>
                      <div className="text-xs text-muted-foreground mono">{v.id}</div>
                    </td>
                    <td className="py-3 px-3 mono">${Number(v.price_usd).toLocaleString()}</td>
                    <td className="py-3 px-3">{statusBadge(v.verification_status)}</td>
                    <td className="py-3 px-3 mono text-sm">{v.photos?.length || 0}</td>
                    <td className="py-3 px-3">
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/vehicles/${v.id}`} className="text-xs text-primary hover:underline">View</Link>
                        <label className="text-xs text-primary hover:underline cursor-pointer">
                          <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => e.target.files && uploadPhotos(v.id, Array.from(e.target.files))} />
                          {uploading === v.id ? "Uploading…" : "Upload photos"}
                        </label>
                        {v.verification_status === "unverified" && (
                          <button className="text-xs text-primary hover:underline" onClick={() => requestVerification(v.id)} data-testid={`request-verify-${v.id}`}>
                            Request Verification
                          </button>
                        )}
                        {v.verification_status === "verified" && <span className="text-xs text-primary flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Verified</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
